import type { WeekStartDay } from './useAppointmentCalendarSettings';

// ─── Day index mapping ───

const DAY_MAP: Record<WeekStartDay, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

const SHORT_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Return 7 weekday labels starting from `weekStartsOn`.
 * e.g. getWeekdayLabels('mon') → ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']
 */
export function getWeekdayLabels(weekStartsOn: WeekStartDay): string[] {
  const start = DAY_MAP[weekStartsOn];
  return Array.from({ length: 7 }, (_, i) => SHORT_LABELS[(start + i) % 7]);
}

/**
 * Get the start-of-week date for a given date, respecting weekStartsOn.
 */
export function startOfWeek(date: Date, weekStartsOn: WeekStartDay): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0=Sun
  const target = DAY_MAP[weekStartsOn];
  let diff = day - target;
  if (diff < 0) diff += 7;
  d.setDate(d.getDate() - diff);
  return d;
}

/**
 * Build a 6-week month grid, aligned to `weekStartsOn`.
 */
export function buildMonthGrid(
  year: number,
  month: number,
  weekStartsOn: WeekStartDay,
): { date: number; month: 'prev' | 'current' | 'next' }[] {
  const firstOfMonth = new Date(year, month, 1);
  const target = DAY_MAP[weekStartsOn];
  const dayOfWeek = firstOfMonth.getDay(); // 0=Sun
  let leadingDays = dayOfWeek - target;
  if (leadingDays < 0) leadingDays += 7;

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();
  const cells: { date: number; month: 'prev' | 'current' | 'next' }[] = [];

  // Previous month trailing days
  for (let i = leadingDays - 1; i >= 0; i--) {
    cells.push({ date: daysInPrevMonth - i, month: 'prev' });
  }

  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: d, month: 'current' });
  }

  // Next month (fill to 42 = 6 rows)
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ date: d, month: 'next' });
  }

  return cells;
}

/**
 * Convert minutes from midnight to display string like "8am", "12pm", "2:30pm".
 */
export function formatMinutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0 || h === 24) return m === 0 ? '12am' : `12:${m.toString().padStart(2, '0')}am`;
  if (h === 12) return m === 0 ? '12pm' : `12:${m.toString().padStart(2, '0')}pm`;
  const h12 = h > 12 ? h - 12 : h;
  const period = h >= 12 ? 'pm' : 'am';
  return m === 0 ? `${h12}${period}` : `${h12}:${m.toString().padStart(2, '0')}${period}`;
}

/**
 * Generate hour ticks from working hours start/end at the given interval.
 * Returns an array of minutes-from-midnight values for each tick.
 */
export function generateTimeSlots(startMin: number, endMin: number, intervalMin: number): number[] {
  const slots: number[] = [];
  for (let m = startMin; m <= endMin; m += intervalMin) {
    slots.push(m);
  }
  return slots;
}

/**
 * Convert working hours (in minutes) to hour-based values for the time grid.
 */
export function minutesToHour(minutes: number): number {
  return minutes / 60;
}
